'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function IpSubnetCalculator() {
  const t = useTranslations("tools.ipSubnetCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'ipv4' | 'ipv6'>('ipv4');

  // IPv4 state
  const [ipv4Inputs, setIpv4Inputs] = useState({
    address: '192.168.1.1',
    cidr: 24,
    networkClass: 'any'
  });

  // IPv6 state
  const [ipv6Inputs, setIpv6Inputs] = useState({
    address: '2001:db8:85a3::8a2e:370:7334',
    prefix: 64
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // IPv4 calculation functions
  const ipv4ToInt = (ip: string): number => {
    const parts = ip.split('.');
    return (parseInt(parts[0]) << 24) + (parseInt(parts[1]) << 16) +
           (parseInt(parts[2]) << 8) + parseInt(parts[3]);
  };

  const intToIpv4 = (int: number): string => {
    return [(int >>> 24) & 255, (int >>> 16) & 255,
            (int >>> 8) & 255, int & 255].join('.');
  };

  const validateIPv4 = (ip: string): boolean => {
    const pattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    const match = ip.match(pattern);
    if (!match) return false;
    return match.slice(1).every(octet => parseInt(octet) >= 0 && parseInt(octet) <= 255);
  };

  const calculateIPv4 = (ip: string, cidr: number) => {
    const ipInt = ipv4ToInt(ip);
    const mask = (0xFFFFFFFF << (32 - cidr)) >>> 0;
    const subnetMask = intToIpv4(mask);
    const wildcardMask = intToIpv4(~mask >>> 0);
    const networkInt = (ipInt & mask) >>> 0;
    const network = intToIpv4(networkInt);
    const broadcastInt = (networkInt | (~mask >>> 0)) >>> 0;
    const broadcast = intToIpv4(broadcastInt);
    const firstHost = cidr === 32 ? network : intToIpv4(networkInt + 1);
    const lastHost = cidr === 32 ? network : intToIpv4(broadcastInt - 1);
    const totalHosts = cidr === 32 ? 1 : Math.pow(2, 32 - cidr);
    const usableHosts = cidr === 32 ? 1 : (cidr === 31 ? 2 : totalHosts - 2);

    let ipClass = 'N/A';
    const firstOctet = parseInt(ip.split('.')[0]);
    if (firstOctet >= 1 && firstOctet <= 126) ipClass = 'A';
    else if (firstOctet >= 128 && firstOctet <= 191) ipClass = 'B';
    else if (firstOctet >= 192 && firstOctet <= 223) ipClass = 'C';
    else if (firstOctet >= 224 && firstOctet <= 239) ipClass = 'D (Multicast)';
    else if (firstOctet >= 240 && firstOctet <= 255) ipClass = 'E (Reserved)';

    return {
      ipAddress: ip,
      networkAddress: network + '/' + cidr,
      subnetMask: subnetMask,
      wildcardMask: wildcardMask,
      broadcastAddress: broadcast,
      firstUsableHost: firstHost,
      lastUsableHost: lastHost,
      totalHosts: totalHosts.toLocaleString(),
      usableHosts: usableHosts.toLocaleString(),
      ipClass: ipClass
    };
  };

  // IPv6 calculation functions
  const expandIPv6 = (ip: string): string => {
    let parts = ip.split(':');
    let expanded: string[] = [];

    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === '') {
        const missing = 8 - parts.filter(p => p !== '').length;
        for (let j = 0; j <= missing; j++) {
          expanded.push('0000');
        }
      } else {
        expanded.push(parts[i].padStart(4, '0'));
      }
    }

    return expanded.slice(0, 8).join(':');
  };

  const validateIPv6 = (ip: string): boolean => {
    const pattern = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;
    return pattern.test(ip);
  };

  const calculateIPv6 = (ip: string, prefix: number) => {
    const expanded = expandIPv6(ip);
    const parts = expanded.split(':');

    let networkParts: string[] = [];
    let bitsProcessed = 0;

    for (let i = 0; i < 8; i++) {
      const hextet = parseInt(parts[i], 16);
      const bitsInHextet = Math.min(16, Math.max(0, prefix - bitsProcessed));

      if (bitsInHextet === 16) {
        networkParts.push(parts[i]);
      } else if (bitsInHextet > 0) {
        const mask = (0xFFFF << (16 - bitsInHextet)) & 0xFFFF;
        networkParts.push((hextet & mask).toString(16).padStart(4, '0'));
      } else {
        networkParts.push('0000');
      }

      bitsProcessed += 16;
    }

    const network = networkParts.join(':');
    const totalAddresses = prefix < 64 ? '2^' + (128 - prefix) : t('veryLarge');

    return {
      ipv6Address: expanded,
      networkPrefix: network + '/' + prefix,
      prefixLength: '/' + prefix,
      totalAddresses: totalAddresses,
      addressType: prefix === 128 ? t('hostAddress') : t('network')
    };
  };

  const handleIPv4Calculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateIPv4(ipv4Inputs.address)) {
      setError(t('ipv4ValidationError'));
      setResults(null);
      return;
    }

    const result = calculateIPv4(ipv4Inputs.address, ipv4Inputs.cidr);
    setResults(result);
  };

  const handleIPv6Calculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateIPv6(ipv6Inputs.address)) {
      setError(t('ipv6ValidationError'));
      setResults(null);
      return;
    }

    const result = calculateIPv6(ipv6Inputs.address, ipv6Inputs.prefix);
    setResults(result);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    tabsContainer: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '2rem',
      justifyContent: 'center' as const,
    },
    tabButton: {
      padding: '0.75rem 2rem',
      background: 'white',
      border: '2px solid #e2e8f0',
      borderRadius: '0.75rem',
      fontSize: '1rem',
      fontWeight: 600,
      color: '#64748b',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    tabButtonActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: '2px solid transparent',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
    calculatorForm: {
      background: 'white',
      padding: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      marginBottom: '2rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    input: {
      width: '100%',
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    select: {
      width: '100%',
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      background: 'white',
    },
    radioGroup: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap' as const,
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      padding: '0.5rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
    },
    calculateButton: {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontSize: '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
    resultsCard: {
      background: 'white',
      padding: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    },
    resultGrid: {
      display: 'grid',
      gap: '1rem',
    },
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
    },
    resultLabel: {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.95rem',
    },
    resultValue: {
      fontFamily: "'Courier New', monospace",
      color: colors.textPrimary,
      fontWeight: 600,
      fontSize: '1rem',
      wordBreak: 'break-all' as const,
    },
    errorMessage: {
      background: '#fef2f2',
      color: '#dc2626',
      padding: '1rem',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #dc2626',
      marginTop: '1rem',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
      padding: '2rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
    },
    reference: {
      marginBottom: '0.75rem',
      paddingLeft: '1rem',
      borderLeft: `3px solid ${colors.primary}`,
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-all' as const,
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    baseStyles.tabsContainer,
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const tabButtonStyle = getResponsiveStyle(
    baseStyles.tabButton,
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    baseStyles.resultItem,
    { flexDirection: 'column' as const, alignItems: 'flex-start' as const, gap: '0.5rem' },
    { flexDirection: 'row' as const, alignItems: 'center' as const },
    { flexDirection: 'row' as const, alignItems: 'center' as const }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={baseStyles.toolHeader}>
            <h2 style={h2Style}>🌐 {t('toolTitle')}</h2>
          </div>

          <div style={tabsContainerStyle}>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'ipv4' ? baseStyles.tabButtonActive : {})
              }}
              onClick={() => {
                setActiveTab('ipv4');
                setResults(null);
                setError('');
              }}
            >
              {t('ipv4Tab')}
            </button>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'ipv6' ? baseStyles.tabButtonActive : {})
              }}
              onClick={() => {
                setActiveTab('ipv6');
                setResults(null);
                setError('');
              }}
            >
              {t('ipv6Tab')}
            </button>
          </div>

          {activeTab === 'ipv4' && (
            <form style={baseStyles.calculatorForm} onSubmit={handleIPv4Calculate}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('networkClass')}</label>
                <div style={baseStyles.radioGroup}>
                  {['any', 'a', 'b', 'c'].map((classType) => (
                    <label
                      key={classType}
                      style={{
                        ...baseStyles.radioLabel,
                        ...(ipv4Inputs.networkClass === classType ? { borderColor: '#2563eb', background: '#f0f9ff' } : {})
                      }}
                    >
                      <input
                        type="radio"
                        name="class"
                        value={classType}
                        checked={ipv4Inputs.networkClass === classType}
                        onChange={(e) => setIpv4Inputs({...ipv4Inputs, networkClass: e.target.value})}
                        style={{ width: 'auto', margin: 0 }}
                      />
                      <span style={{ color: ipv4Inputs.networkClass === classType ? '#2563eb' : '#64748b' }}>
                        {t(`class${classType.toUpperCase()}`)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label htmlFor="ipv4-subnet" style={baseStyles.label}>{t('subnetMask')}</label>
                <select
                  id="ipv4-subnet"
                  style={baseStyles.select}
                  value={ipv4Inputs.cidr}
                  onChange={(e) => setIpv4Inputs({...ipv4Inputs, cidr: parseInt(e.target.value)})}
                >
                  {Array.from({length: 25}, (_, i) => i + 8).map(cidr => (
                    <option key={cidr} value={cidr}>
                      /{cidr} - {intToIpv4((0xFFFFFFFF << (32 - cidr)) >>> 0)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={baseStyles.formGroup}>
                <label htmlFor="ipv4-address" style={baseStyles.label}>{t('ipAddress')}</label>
                <input
                  type="text"
                  id="ipv4-address"
                  style={baseStyles.input}
                  placeholder="e.g., 192.168.1.1"
                  value={ipv4Inputs.address}
                  onChange={(e) => setIpv4Inputs({...ipv4Inputs, address: e.target.value})}
                  required
                />
              </div>

              <button type="submit" style={baseStyles.calculateButton}>
                {t('calculateIPv4')}
              </button>

              {error && (
                <div style={baseStyles.errorMessage}>
                  {error}
                </div>
              )}
            </form>
          )}

          {activeTab === 'ipv6' && (
            <form style={baseStyles.calculatorForm} onSubmit={handleIPv6Calculate}>
              <div style={baseStyles.formGroup}>
                <label htmlFor="ipv6-prefix" style={baseStyles.label}>{t('prefixLength')}</label>
                <select
                  id="ipv6-prefix"
                  style={baseStyles.select}
                  value={ipv6Inputs.prefix}
                  onChange={(e) => setIpv6Inputs({...ipv6Inputs, prefix: parseInt(e.target.value)})}
                >
                  {[32, 48, 56, 64, 80, 96, 112, 128].map(prefix => (
                    <option key={prefix} value={prefix}>/{prefix}</option>
                  ))}
                </select>
              </div>

              <div style={baseStyles.formGroup}>
                <label htmlFor="ipv6-address" style={baseStyles.label}>{t('ipv6Address')}</label>
                <input
                  type="text"
                  id="ipv6-address"
                  style={baseStyles.input}
                  placeholder="e.g., 2001:db8:85a3::8a2e:370:7334"
                  value={ipv6Inputs.address}
                  onChange={(e) => setIpv6Inputs({...ipv6Inputs, address: e.target.value})}
                  required
                />
              </div>

              <button type="submit" style={baseStyles.calculateButton}>
                {t('calculateIPv6')}
              </button>

              {error && (
                <div style={baseStyles.errorMessage}>
                  {error}
                </div>
              )}
            </form>
          )}

          {results && (
            <div style={baseStyles.resultsCard}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>
                {t('subnetInformation')}
              </h3>
              <div style={baseStyles.resultGrid}>
                {Object.entries(results).map(([key, value]) => (
                  <div key={key} style={resultItemStyle}>
                    <span style={baseStyles.resultLabel}>{t(`result_${key}`)}:</span>
                    <span style={baseStyles.resultValue}>{value as string}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('step1Title')}</span> {t('step1Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('step2Title')}</span> {t('step2Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('step3Title')}</span> {t('step3Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('step4Title')}</span> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={{ ...baseStyles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('whatCalculatorDoesTitle')}
        </h3>
        <p style={paragraphStyle}>{t('whatCalculatorDoesParagraph1')}</p>
        <p style={paragraphStyle}>{t('whatCalculatorDoesParagraph2')}</p>
        <p style={paragraphStyle}>{t('whatCalculatorDoesParagraph3')}</p>

        <h3 style={{ ...baseStyles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('whyUsefulTitle')}
        </h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('benefit1Title')}</span> {t('benefit1Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('benefit2Title')}</span> {t('benefit2Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('benefit3Title')}</span> {t('benefit3Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('benefit4Title')}</span> {t('benefit4Description')}</li>
        </ul>

        <h3 style={{ ...baseStyles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('keyConceptsTitle')}
        </h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('concept1Title')}</span> {t('concept1Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('concept2Title')}</span> {t('concept2Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('concept3Title')}</span> {t('concept3Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('concept4Title')}</span> {t('concept4Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('concept5Title')}</span> {t('concept5Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice1Title')}</span> {t('practice1Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice2Title')}</span> {t('practice2Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice3Title')}</span> {t('practice3Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice4Title')}</span> {t('practice4Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice5Title')}</span> {t('practice5Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice6Title')}</span> {t('practice6Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strong}>{t('practice7Title')}</span> {t('practice7Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...baseStyles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('ipv4DetailsTitle')}
        </h3>
        <p style={paragraphStyle}>{t('ipv4DetailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('ipv4DetailsParagraph2')}</p>

        <h3 style={{ ...baseStyles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('ipv6DetailsTitle')}
        </h3>
        <p style={paragraphStyle}>{t('ipv6DetailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('ipv6DetailsParagraph2')}</p>

        <h3 style={{ ...baseStyles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('cidrNotationTitle')}
        </h3>
        <p style={paragraphStyle}>{t('cidrNotationParagraph')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={baseStyles.reference}>
          <p style={paragraphStyle}>
            <a href="https://pandorafms.com/blog/ip-subnet-calculator/" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t('reference1')}
            </a>
          </p>
        </div>
        <div style={baseStyles.reference}>
          <p style={paragraphStyle}>
            <a href="https://pandorafms.com/blog/ip-calculator-networks/" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t('reference2')}
            </a>
          </p>
        </div>
        <div style={baseStyles.reference}>
          <p style={paragraphStyle}>
            <a href="https://www.solarwinds.com/free-tools/advanced-subnet-calculator" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t('reference3')}
            </a>
          </p>
        </div>
        <div style={baseStyles.reference}>
          <p style={paragraphStyle}>
            <a href="https://uptimerobot.com/knowledge-base/what-is-subnet/" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t('reference4')}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}